/**
 * Call controls: mute and hang up, two squares sized for a thumb. Red lives only
 * on the hang-up glyph until it is pressed, so the call stays quiet. Pure presentation.
 */

import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

import { endCallIcon, micIcon, micOffIcon } from "./icons.js";

@customElement("deepagent-call-controls")
export class DeepAgentCallControls extends LitElement {
  /**
   * Lets the panel move focus into this group during a call by calling
   * `.focus()` on the element, without reaching through its shadow root.
   */
  static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  static override styles = css`
    :host {
      display: flex;
      justify-content: center;
      gap: 12px;
      padding: 12px 16px 16px;
      flex: 0 0 auto;
    }

    button {
      appearance: none;
      cursor: pointer;
      width: 52px;
      height: 52px;
      border-radius: var(--da-radius, 2px);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-family: inherit;
      font-size: 14px;
      font-weight: 600;
      transition:
        background-color 180ms cubic-bezier(0.16, 1, 0.3, 1),
        border-color 180ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    button:active {
      transform: scale(0.98);
      transition-duration: 90ms;
    }

    button:focus-visible {
      outline: 2px solid var(--da-accent);
      outline-offset: 2px;
    }

    .mute {
      border: none;
      background: var(--da-surface-2);
      color: var(--da-text);
    }

    @media (hover: hover) {
      .mute:hover {
        background: color-mix(in srgb, var(--da-text) 12%, var(--da-surface-2));
      }
    }

    .mute[aria-pressed="true"] {
      background: var(--da-text);
      border-color: var(--da-text);
      color: var(--da-bg);
    }

    .end {
      border: none;
      background: var(--da-danger-bg);
      color: var(--da-danger);
    }

    @media (hover: hover) {
      .end:hover {
        background: var(--da-danger);
        color: #ffffff;
      }
    }

    svg {
      width: 20px;
      height: 20px;
    }
  `;

  @property({ type: Boolean }) muted = false;
  @property({ type: String }) muteLabel = "Mute microphone";
  @property({ type: String }) unmuteLabel = "Unmute microphone";
  @property({ type: String }) endLabel = "End call";

  private emit(name: "mute-toggle" | "end-call") {
    this.dispatchEvent(
      new CustomEvent(name, { bubbles: true, composed: true }),
    );
  }

  override render() {
    const muteLabel = this.muted ? this.unmuteLabel : this.muteLabel;
    return html`
      <button
        type="button"
        class="mute"
        aria-pressed=${this.muted ? "true" : "false"}
        aria-label=${muteLabel}
        title=${muteLabel}
        @click=${() => this.emit("mute-toggle")}
      >
        ${this.muted ? micOffIcon() : micIcon()}
      </button>
      <button
        type="button"
        class="end"
        aria-label=${this.endLabel}
        title=${this.endLabel}
        @click=${() => this.emit("end-call")}
      >
        ${endCallIcon()}
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-call-controls": DeepAgentCallControls;
  }
}
